// Forecast engine — pure functions. Given customers (with their date-ranged
// allocations) it computes forecast volume per customer/hub/date and per hub/day.
//
// Formula (per customer, hub, date):
//   round( dailyBase
//          × growthFactor(growth%, yearsSinceOnboarding)
//          × allocationPct/100
//          × dayOfWeekFactor
//          × seasonalMultiplier )
//
// Gated to 0 when: customer inactive, tier 1 (head-to-head lane), before the
// onboarding date, or no allocation active for that hub on that date.

import type { Customer, CustomerAllocation, HubEvent } from "../types";
import { DAY_FACTORS, DOW_PROFILE_FACTORS, HOLIDAY_TABLE, SEASONAL_FACTORS } from "./constants";

const NEG_INF = "0000-00-00";
const POS_INF = "9999-99-99";

/** Week-of-year (from Jan 1, not strict ISO). Returns 1..53. */
export function getWeekOfYear(d: Date): number {
  const start = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7);
}

/** Compound growth from a baseline date forward. */
export function growthAdjusted(
  base: number,
  annualGrowthPct: number,
  targetDate: string,
  baselineDate?: string
): number {
  const ref = new Date((baselineDate || targetDate) + "T12:00:00");
  const target = new Date(targetDate + "T12:00:00");
  const years = Math.max(0, (target.getTime() - ref.getTime()) / (365.25 * 86400000));
  return Math.round(base * Math.pow(1 + (annualGrowthPct || 0) / 100, years));
}

// The active week-of-year seasonal curve (index 1..52). Defaults to the built-in
// table; an account's calibrated curve replaces it via setSeasonalFactors (loaded
// once at sign-in). Holidays are separate (HOLIDAY_TABLE) and always win.
let _activeSeasonal: number[] = SEASONAL_FACTORS;

/** Replace the active seasonal curve (length ≥ 53, index 1..52). null → default. */
export function setSeasonalFactors(factors: number[] | null): void {
  _activeSeasonal = factors && factors.length >= 53 ? factors : SEASONAL_FACTORS;
}
/** The seasonal curve currently in use (default or a calibrated one). */
export function getSeasonalFactors(): number[] {
  return _activeSeasonal;
}
/** The built-in default seasonal curve (for side-by-side comparison in the UI). */
export function defaultSeasonalFactors(): number[] {
  return SEASONAL_FACTORS;
}

/** Seasonal × holiday multiplier for a date. */
export function seasonalMultiplier(dateStr: string): number {
  const d = new Date(dateStr + "T12:00:00");
  const wk = Math.min(52, Math.max(1, getWeekOfYear(d)));
  const holiday = HOLIDAY_TABLE[dateStr.slice(5)];
  return holiday != null ? holiday : _activeSeasonal[wk];
}

/**
 * Learn a week-of-year seasonal curve from observations. Each observation pairs a
 * date's actual volume with the expected volume WITHOUT seasonality
 * (base × growth × allocation × DOW). A week's factor is the volume-weighted ratio
 * actual/expected, normalized so the overall weighted mean is 1 (seasonality is
 * shape, not level — base calibration owns the level). Weeks with no data keep the
 * default curve. Returns null when there's no usable signal.
 */
export function learnSeasonalFactors(
  observations: Array<{ date: string; actual: number; expected: number }>
): number[] | null {
  const sumA = Array(53).fill(0);
  const sumE = Array(53).fill(0);
  let totA = 0;
  let totE = 0;
  for (const o of observations) {
    if (!(o.expected > 0) || !(o.actual >= 0)) continue;
    const wk = Math.min(52, Math.max(1, getWeekOfYear(new Date(o.date + "T12:00:00"))));
    sumA[wk] += o.actual;
    sumE[wk] += o.expected;
    totA += o.actual;
    totE += o.expected;
  }
  if (totE <= 0) return null;
  const overall = totA / totE; // mean implied multiplier across observed data
  if (overall <= 0) return null;
  const out = SEASONAL_FACTORS.slice();
  let any = false;
  for (let w = 1; w <= 52; w++) {
    if (sumE[w] > 0) {
      out[w] = Number((sumA[w] / sumE[w] / overall).toFixed(3));
      any = true;
    }
  }
  return any ? out : null;
}

/** Week-of-year (1..52, clamped) for a YYYY-MM-DD — exposed for seasonal tooling. */
export function weekOfYearOf(dateStr: string): number {
  return Math.min(52, Math.max(1, getWeekOfYear(new Date(dateStr + "T12:00:00"))));
}

/** True if the date is a federal holiday in the table. */
export function isHoliday(dateStr: string): boolean {
  return HOLIDAY_TABLE[dateStr.slice(5)] != null;
}

/** Day-of-week factor array for a profile (falls back to network factors). */
export function dowFactorsFor(profile: string): number[] {
  return DOW_PROFILE_FACTORS[profile] ?? DAY_FACTORS;
}

/** DOW factors for a customer — calibrated (custom) factors if set, else profile. */
export function customerDowFactors(customer: Customer): number[] {
  if (
    customer.dowProfile === "custom" &&
    Array.isArray(customer.customDowFactors) &&
    customer.customDowFactors.length === 7
  ) {
    return customer.customDowFactors;
  }
  return dowFactorsFor(customer.dowProfile);
}

/**
 * DOW factors for a customer at a specific hub: the lane's calibrated per-hub
 * factors if present, otherwise the customer-level factors.
 */
export function dowForCustomerHub(customer: Customer, hubId: string): number[] {
  const lane = customer.laneDowFactors?.[hubId];
  if (Array.isArray(lane) && lane.length === 7) return lane;
  return customerDowFactors(customer);
}

/**
 * Calibrate day-of-week factors from observed daily volumes: average each
 * weekday (getDay 0..6), fill missing weekdays with the overall average, then
 * normalize so the 7 factors sum to 7. Returns null if there's no signal.
 */
export function calibrateDowFactors(
  observations: Array<{ date: string; value: number }>
): number[] | null {
  const sums = [0, 0, 0, 0, 0, 0, 0];
  const counts = [0, 0, 0, 0, 0, 0, 0];
  for (const o of observations) {
    if (!(o.value > 0)) continue;
    const dow = new Date(o.date + "T12:00:00").getDay();
    sums[dow] += o.value;
    counts[dow] += 1;
  }
  const totalCount = counts.reduce((a, b) => a + b, 0);
  if (totalCount === 0) return null;
  const overallAvg = sums.reduce((a, b) => a + b, 0) / totalCount;
  const avg = sums.map((s, i) => (counts[i] > 0 ? s / counts[i] : overallAvg));
  const sumAvg = avg.reduce((a, b) => a + b, 0);
  if (sumAvg <= 0) return null;
  return avg.map((a) => Number(((a / sumAvg) * 7).toFixed(3)));
}

/**
 * Calibrate the daily base from observed daily volumes: de-seasonalize and
 * de-DOW each actual (divide by its DOW factor × seasonal multiplier) and
 * average. The result is the "neutral day, neutral season" level so that
 * base × DOW × seasonal reproduces the actuals. Returns null if there's no
 * usable signal. (Growth is applied on top, from the onboarding baseline.)
 */
export function calibrateDailyBase(
  observations: Array<{ date: string; value: number }>,
  dowFactors: number[]
): number | null {
  let sum = 0;
  let n = 0;
  for (const o of observations) {
    if (!(o.value > 0)) continue;
    const dow = new Date(o.date + "T12:00:00").getDay();
    const denom = (dowFactors[dow] ?? 1) * seasonalMultiplier(o.date);
    if (denom <= 0) continue;
    sum += o.value / denom;
    n += 1;
  }
  if (n === 0) return null;
  return Math.round(sum / n);
}

/** Combined allocation % for a hub active on a date (handles date windows). */
export function activeAllocationPct(
  allocations: CustomerAllocation[],
  hubId: string,
  dateStr: string
): number {
  let pct = 0;
  for (const a of allocations) {
    if (a.hubId !== hubId) continue;
    const start = a.effectiveDate || NEG_INF;
    const end = a.endDate || POS_INF;
    if (start <= dateStr && dateStr < end) pct += a.percentage;
  }
  return pct;
}

/** Stable key for a manual forecast override (customer + hub + date). */
export function overrideKey(customerId: string, hubId: string, dateStr: string): string {
  return `${customerId}|${hubId}|${dateStr}`;
}

/** The computed forecast for a customer/hub/date BEFORE overrides or events. */
export function rawForecastCustomerHub(customer: Customer, hubId: string, dateStr: string): number {
  if (!customer.active) return 0;
  if (customer.tier === 1) return 0; // head-to-head lane: not forecast directly
  if (customer.onboardingDate && dateStr < customer.onboardingDate) return 0;

  const pct = activeAllocationPct(customer.allocations, hubId, dateStr);
  if (pct <= 0) return 0;

  const base = growthAdjusted(
    customer.dailyBase,
    customer.growth || 0,
    dateStr,
    customer.onboardingDate || dateStr
  );
  const dow = new Date(dateStr + "T12:00:00").getDay();
  const dowFactor = dowForCustomerHub(customer, hubId)[dow];
  return Math.round(base * (pct / 100) * dowFactor * seasonalMultiplier(dateStr));
}

/**
 * The active hub event for a hub on a date (first match). When a `customerId` is
 * given, a `by_customer` event only matches if that customer is in its target
 * list; a `flat` event matches everyone. When no `customerId` is given (hub-wide
 * query), only `flat` events match — a customer-scoped event isn't hub-wide.
 */
export function getHubEvent(
  events: HubEvent[],
  hubId: string,
  dateStr: string,
  customerId?: string
): HubEvent | null {
  return (
    events.find((e) => {
      if (!e || e.hubId !== hubId || e.active === false) return false;
      if (dateStr < e.startDate || dateStr > e.endDate) return false;
      if (e.mode === "by_customer") {
        return (
          customerId != null && (e.customerOverrides ?? []).some((o) => o.customerId === customerId)
        );
      }
      return true;
    }) || null
  );
}

/**
 * The share (0..100) of a customer's volume that an event affects. A flat event
 * affects 100%; a by_customer event affects the customer's configured share (0 if
 * the customer isn't targeted).
 */
export function customerEventPct(evt: HubEvent, customerId: string): number {
  if (evt.mode !== "by_customer") return 100;
  const o = (evt.customerOverrides ?? []).find((x) => x.customerId === customerId);
  return o ? o.pct : 0;
}

/** Retained-capacity % for an event (null ⇒ closure 0, otherwise 100). */
export function effectiveCapacityPct(evt: HubEvent): number {
  return evt.capacityPct == null ? (evt.type === "closure" ? 0 : 100) : evt.capacityPct;
}

/**
 * Apply hub events to a raw forecast at a hub: scale by the hub's retained
 * capacity, then add volume rerouted IN from other hubs' reroute events.
 */
export function applyEvents(
  customer: Customer,
  hubId: string,
  dateStr: string,
  events: HubEvent[],
  raw: number
): number {
  let result = raw;
  const evt = getHubEvent(events, hubId, dateStr, customer.id);
  if (evt) {
    // Only the affected share of the customer's volume is scaled by retained capacity.
    const affected = customerEventPct(evt, customer.id) / 100;
    const retained = effectiveCapacityPct(evt) / 100;
    result = Math.round(raw * (1 - affected) + raw * affected * retained);
  }
  return result + reroutedInForCustomerHub(customer, hubId, dateStr, events);
}

/**
 * Volume rerouted INTO `hubId` for one customer on a date — i.e. volume that
 * would normally land at another of the customer's hubs but is redistributed here
 * by a reroute event there. This is the additive piece inside applyEvents.
 */
export function reroutedInForCustomerHub(
  customer: Customer,
  hubId: string,
  dateStr: string,
  events: HubEvent[]
): number {
  let extra = 0;
  for (const oa of customer.allocations) {
    if (!oa || oa.hubId === hubId) continue;
    const oEvt = getHubEvent(events, oa.hubId, dateStr, customer.id);
    if (!oEvt || !oEvt.redistributeVolume || !oEvt.redistributeHubs?.length) continue;
    const share = oEvt.redistributeHubs.find((h) => h.hubId === hubId);
    if (!share) continue;
    const wouldBe = rawForecastCustomerHub(customer, oa.hubId, dateStr);
    const affected = customerEventPct(oEvt, customer.id) / 100; // share of customer's volume affected
    const redirect = 1 - effectiveCapacityPct(oEvt) / 100; // displaced fraction of the affected share
    extra += wouldBe * affected * redirect * ((share.pct || 0) / 100);
  }
  return Math.round(extra);
}

/**
 * Volume rerouted OUT of `hubId` for one customer on a date — the displaced share
 * that a reroute event at this hub sends to other hubs.
 */
export function reroutedOutForCustomerHub(
  customer: Customer,
  hubId: string,
  dateStr: string,
  events: HubEvent[]
): number {
  const evt = getHubEvent(events, hubId, dateStr, customer.id);
  if (!evt || !evt.redistributeVolume || !evt.redistributeHubs?.length) return 0;
  const raw = rawForecastCustomerHub(customer, hubId, dateStr);
  const affected = customerEventPct(evt, customer.id) / 100;
  const redirect = 1 - effectiveCapacityPct(evt) / 100;
  const shareTotal = evt.redistributeHubs.reduce((s, h) => s + (h.pct || 0), 0) / 100;
  return Math.round(raw * affected * redirect * shareTotal);
}

/** Total volume rerouted INTO a hub on a date (summed across customers). */
export function reroutedInToHub(
  customers: Customer[],
  hubId: string,
  dateStr: string,
  events: HubEvent[]
): number {
  return customers.reduce((s, c) => s + reroutedInForCustomerHub(c, hubId, dateStr, events), 0);
}

/** Total volume rerouted OUT of a hub on a date to other hubs (summed across customers). */
export function reroutedOutOfHub(
  customers: Customer[],
  hubId: string,
  dateStr: string,
  events: HubEvent[]
): number {
  return customers.reduce((s, c) => s + reroutedOutForCustomerHub(c, hubId, dateStr, events), 0);
}

/**
 * Forecast volume for one customer/hub/date. Override wins; otherwise the raw
 * forecast is computed and hub events (if any) are applied.
 */
export function forecastCustomerHub(
  customer: Customer,
  hubId: string,
  dateStr: string,
  overrides?: Map<string, number>,
  events?: HubEvent[]
): number {
  if (overrides) {
    const o = overrides.get(overrideKey(customer.id, hubId, dateStr));
    if (o !== undefined) return o;
  }
  const raw = rawForecastCustomerHub(customer, hubId, dateStr);
  return events && events.length ? applyEvents(customer, hubId, dateStr, events, raw) : raw;
}

/** Total forecast volume at a hub on a date (overrides + events applied). */
export function forecastHubDay(
  customers: Customer[],
  hubId: string,
  dateStr: string,
  overrides?: Map<string, number>,
  events?: HubEvent[]
): number {
  return customers.reduce(
    (sum, c) => sum + forecastCustomerHub(c, hubId, dateStr, overrides, events),
    0
  );
}

/** Step-by-step derivation of a customer's computed forecast (ignores overrides). */
export interface ForecastBreakdown {
  eligible: boolean;
  reason: string | null; // why it's 0, when not eligible
  base: number; // daily base
  growthFactor: number; // ×
  allocationPct: number;
  dowFactor: number; // ×
  seasonalFactor: number; // ×
  isHoliday: boolean;
  forecast: number; // computed (no override)
}

/** Explain how a customer's forecast for a hub/date is built. */
export function explainCustomerHub(
  customer: Customer,
  hubId: string,
  dateStr: string
): ForecastBreakdown {
  const dow = new Date(dateStr + "T12:00:00").getDay();
  const seasonalFactor = seasonalMultiplier(dateStr);
  const isHoliday = HOLIDAY_TABLE[dateStr.slice(5)] != null;
  const allocationPct = activeAllocationPct(customer.allocations, hubId, dateStr);
  const dowFactor = dowForCustomerHub(customer, hubId)[dow];
  const base = customer.dailyBase;
  const grown = growthAdjusted(base, customer.growth || 0, dateStr, customer.onboardingDate || dateStr);
  const growthFactor = base > 0 ? grown / base : 1;

  let eligible = true;
  let reason: string | null = null;
  if (!customer.active) ((eligible = false), (reason = "Customer inactive"));
  else if (customer.tier === 1) ((eligible = false), (reason = "Head-to-head lane (not forecast)"));
  else if (customer.onboardingDate && dateStr < customer.onboardingDate)
    ((eligible = false), (reason = "Before onboarding date"));
  else if (allocationPct <= 0) ((eligible = false), (reason = "No active allocation at this hub"));

  const forecast = eligible
    ? Math.round(grown * (allocationPct / 100) * dowFactor * seasonalFactor)
    : 0;
  return { eligible, reason, base, growthFactor, allocationPct, dowFactor, seasonalFactor, isHoliday, forecast };
}

/** Loads needed to carry `volume` given a per-load capacity (0 if no capacity). */
export function impliedLoads(volume: number, capacity?: number | null): number {
  if (!capacity || capacity <= 0) return 0;
  return Math.ceil(volume / capacity);
}

/**
 * Mean Absolute Percentage Error across forecast/actual pairs (only rows with a
 * positive actual count). Returns null if there's nothing to compare.
 */
export function meanAbsPctError(rows: Array<{ forecast: number; actual: number }>): number | null {
  const valid = rows.filter((r) => r.actual > 0);
  if (valid.length === 0) return null;
  const sum = valid.reduce((s, r) => s + Math.abs(r.forecast - r.actual) / r.actual, 0);
  return (sum / valid.length) * 100;
}

/** Whether a customer is forecast-eligible at a hub on a date (active+allocated). */
export function isCustomerActiveAt(customer: Customer, hubId: string, dateStr: string): boolean {
  if (!customer.active || customer.tier === 1) return false;
  if (customer.onboardingDate && dateStr < customer.onboardingDate) return false;
  return activeAllocationPct(customer.allocations, hubId, dateStr) > 0;
}
